import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Tag, ArrowLeft, ChevronRight } from 'lucide-react';
import { Footer } from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CategoryPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/categories/${slug}`)
      .then(res => {
        setData(res.data);
        if (res.data.category) {
          document.title = `${res.data.category} - Cupons de Desconto | ClubdoCupom.com`;
          const meta = document.querySelector('meta[name="description"]');
          if (meta) meta.setAttribute('content', res.data.description || `Cupons de desconto em ${res.data.category} no ClubdoCupom.com`);

          // Inject JSON-LD for this category
          const existing = document.getElementById('category-schema-ld');
          if (existing) existing.remove();
          const schema = {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": `${res.data.category} - Cupons de Desconto`,
            "description": res.data.description,
            "url": `https://www.clubdocupom.com/categorias/${slug}`,
            "isPartOf": { "@type": "WebSite", "name": "ClubdoCupom.com", "url": "https://www.clubdocupom.com" },
            "numberOfItems": res.data.coupons?.length || 0,
          };
          const script = document.createElement('script');
          script.id = 'category-schema-ld';
          script.type = 'application/ld+json';
          script.textContent = JSON.stringify(schema);
          document.head.appendChild(script);
        }
      })
      .catch(() => setData({ category: null, coupons: [] }))
      .finally(() => setLoading(false));

    return () => {
      document.title = 'ClubdoCupom.com - O Canal Oficial do Desconto Direto';
      const el = document.getElementById('category-schema-ld');
      if (el) el.remove();
    };
  }, [slug]);

  const handleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `${BACKEND_URL}/api/auth/google/login?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-amber-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-slate-500">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!data?.category) {
    return (
      <div className="min-h-screen bg-slate-50">
        <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex justify-between items-center">
            <Link to="/" className="text-xl sm:text-2xl font-black text-slate-900">ClubdoCupom</Link>
            <Link to="/categorias"><Button variant="outline" className="text-xs sm:text-sm px-3 sm:px-4" data-testid="back-categories-btn">Categorias</Button></Link>
          </div>
        </nav>
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <Tag className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2" data-testid="category-not-found">Categoria nao encontrada</h1>
          <p className="text-slate-500 mb-6">A categoria que voce procura nao existe.</p>
          <Link to="/categorias"><Button className="bg-amber-500 hover:bg-amber-600 text-white font-bold" data-testid="browse-categories-btn">Ver Todas as Categorias</Button></Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navbar */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex justify-between items-center">
          <Link to="/" className="text-xl sm:text-2xl font-black text-slate-900">ClubdoCupom</Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/categorias">
              <Button variant="outline" className="border-slate-300 text-slate-700 text-xs sm:text-sm px-2 sm:px-4" data-testid="nav-categories-btn">
                <ArrowLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4 sm:mr-1" /> <span className="hidden sm:inline">Categorias</span>
              </Button>
            </Link>
            <Button onClick={handleLogin} className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-2 px-3 sm:px-6 rounded-lg text-xs sm:text-sm" data-testid="login-btn">
              Entrar
            </Button>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <nav className="flex items-center text-sm text-slate-500" data-testid="breadcrumb">
            <Link to="/" className="hover:text-slate-900 transition-colors">Home</Link>
            <ChevronRight className="h-3.5 w-3.5 mx-2" />
            <Link to="/categorias" className="hover:text-slate-900 transition-colors">Categorias</Link>
            <ChevronRight className="h-3.5 w-3.5 mx-2" />
            <span className="text-slate-900 font-medium">{data.category}</span>
          </nav>
        </div>
      </div>

      {/* Category Header */}
      <section className="bg-slate-900 py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-sm px-4 py-1 mb-4">
            <Tag className="h-3.5 w-3.5 mr-1.5" /> Categoria
          </Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-3" data-testid="category-title">
            {data.category}
          </h1>
          <p className="text-base text-slate-400 max-w-2xl mx-auto" data-testid="category-description">
            {data.description}
          </p>
          {data.coupons.length > 0 && (
            <p className="text-sm text-slate-500 mt-4">
              {data.coupons.length} {data.coupons.length === 1 ? 'cupom disponivel' : 'cupons disponiveis'}
            </p>
          )}
        </div>
      </section>

      {/* Coupons */}
      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {data.coupons.length === 0 ? (
            <div className="text-center py-16">
              <Tag className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-slate-900 mb-2" data-testid="no-coupons-msg">Ainda nao ha cupons nesta categoria</h2>
              <p className="text-slate-500 mb-6">Novos cupons são adicionados diariamente pelos lojistas.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/categorias">
                  <Button variant="outline" className="border-slate-400 text-slate-700 font-bold" data-testid="see-other-categories-btn">
                    Explorar Outras Categorias
                  </Button>
                </Link>
                <Button onClick={handleLogin} className="bg-amber-500 hover:bg-amber-600 text-white font-bold" data-testid="register-btn">
                  Cadastre-se e Fique por Dentro
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="coupons-grid">
              {data.coupons.map(coupon => (
                <Card key={coupon.coupon_id}
                  className="bg-white border border-slate-200 shadow-sm hover:shadow-lg transition-all cursor-pointer hover:-translate-y-1 duration-300"
                  onClick={handleLogin}
                  data-testid={`coupon-card-${coupon.coupon_id}`}>
                  {coupon.images?.length > 0 && (
                    <div className="w-full aspect-[4/3] overflow-hidden rounded-t-lg">
                      <img src={coupon.images[0]} alt={coupon.title} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                  )}
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start mb-2">
                      <Badge className="bg-red-500 text-white border-0">{coupon.discount_percentage}% OFF</Badge>
                      <Badge className="bg-slate-100 text-slate-700 border-slate-200 text-xs">{coupon.category}</Badge>
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-1">{coupon.title}</h3>
                    <p className="text-sm text-slate-600 line-clamp-2 mb-2">{coupon.description}</p>
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <MapPin className="h-3 w-3" /> {coupon.city}, {coupon.state}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Quer aproveitar esses descontos?</h2>
          <p className="text-slate-400 mb-6">Cadastre-se gratuitamente e resgate cupons exclusivos.</p>
          <Button onClick={handleLogin} size="lg"
            className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 px-8 rounded-lg shadow-lg text-lg"
            data-testid="cta-register-btn">
            Criar Conta Gratis
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
